import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { saleService } from "../../services/saleService";
import { binService } from "../../services/binService";
import { useBanks, useBin } from "../../query/hooks";
import { emptyBin } from "../../query/bin";
import { queryKeys } from "../../query/keys";
import ChatModal from "../Chat/ChatModal";
import AgentPreview from "../Agent/AgentPreview";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  CubeIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";

const Sell = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const binQuery = useBin(true);
  const banksQuery = useBanks(true);
  const binData = binQuery.data || emptyBin();
  const banks = banksQuery.data;
  const [selectedBank, setSelectedBank] = useState("");
  const [selling, setSelling] = useState(false);
  const [message, setMessage] = useState("");
  const [searchParams] = useSearchParams();
  const [showChat, setShowChat] = useState(false);
  const [chatBinId, setChatBinId] = useState(null);
  const loading = binQuery.isLoading || banksQuery.isLoading;

  useEffect(() => {
    const bin = searchParams.get("chat");
    if (bin) {
      setChatBinId(bin);
      setShowChat(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const bankDetails = banks?.data?.bankDetails || banks?.bankDetails || [];
    if (bankDetails.length > 0 && !selectedBank) {
      setSelectedBank(bankDetails[0]._id);
    }
  }, [banks, selectedBank]);

  useEffect(() => {
    if (binQuery.isError || banksQuery.isError) setMessage("Error loading data");
  }, [binQuery.isError, banksQuery.isError]);

  const isBinValidated =
    binData?.validationStatus === true || binData?.validationStatus === "true";

  const handleSell = async () => {
    if (!selectedBank) {
      setMessage("Please select a bank account for payment. If you don't have any bank accounts, please add one first.");
      return;
    }

    // Validate that the selected bank exists
    const bankDetails = banks?.data?.bankDetails || banks?.bankDetails || [];
    const selectedBankExists = bankDetails.some(bank => bank._id === selectedBank);
    if (!selectedBankExists) {
      setMessage("Selected bank account is no longer valid. Please select a different bank account.");
      return;
    }

    if (!isBinValidated) {
      setMessage("Your bin must be validated by an agent before you can sell");
      return;
    }

    setSelling(true);
    setMessage("");

    try {
      await saleService.createSale({
        binId: binData?._id,
        bankId: selectedBank,
      });
      queryClient.setQueryData(queryKeys.bin, emptyBin());
      queryClient.invalidateQueries({ queryKey: queryKeys.sales });
      queryClient.invalidateQueries({ queryKey: queryKeys.bin });
      if (typeof window !== "undefined" && window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
      setMessage("Sale completed successfully! Redirecting to sale history...");
      setTimeout(() => {
        navigate("/sales");
      }, 2000);
    } catch (error) {
      console.error("Error creating sale:", error);
      setMessage(error.response?.data?.message || "Error completing sale");
    } finally {
      setSelling(false);
    }
  };

  const handleChatClick = (binId) => {
    setChatBinId(binId);
    setShowChat(true);
  };

  const handleClearBin = async () => {
    if (window.confirm("Are you sure you want to clear your bin? This will cancel your order and remove all materials.")) {
      try {
        await binService.clearBin();
        queryClient.setQueryData(queryKeys.bin, emptyBin());
        queryClient.invalidateQueries({ queryKey: queryKeys.bin });
        setMessage("Bin cleared successfully! Redirecting to materials...");
        setTimeout(() => {
          navigate("/materials");
        }, 2000);
      } catch (error) {
        console.error("Error clearing bin:", error);
        setMessage(error.response?.data?.message || "Error clearing bin");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Complete Your Sale
        </h1>
        <p className="text-gray-600">
          Review your validated materials and select a payment account to
          complete your sale.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successfully")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {message.includes("successfully") ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* Agent Preview */}
      {binData?.selectedAgent && (
        <div>
          <AgentPreview 
            agent={binData.selectedAgent}
            onChatClick={handleChatClick}
            binId={binData?._id}
          />
          {/* Change Agent Button */}
          {/* {binData?.validationStatus && (
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate("/agents")}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Change Agent
              </button>
            </div>
          )} */}
        </div>
      )}

      {/* Validation Status */}
      {!binData?.validationStatus && !binData?.selectedAgent && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
              <div>
                <h3 className="text-lg font-semibold text-amber-950">
                  No agent selected
                </h3>
                <p className="mt-1 text-sm text-amber-800">
                  Select an agent to validate your materials before you can complete your sale.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/agents")}
              className="inline-flex w-full items-center justify-center rounded-xl bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700 sm:w-auto"
            >
              Select agent
            </button>
          </div>
        </div>
      )}

      {/* Empty Bin */}
      {!binData?.materials?.length ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CubeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No materials to sell
          </h2>
          <p className="text-gray-600 mb-6">
            Add materials to your bin and get them validated by an agent first.
          </p>
          <button
            onClick={() => navigate("/materials")}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Browse Materials
          </button>
        </div>
      ) : (
        <>
          {/* Materials Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Validated Materials
            </h2>
            <div className="space-y-4">
              {binData?.materials?.map((item) => (
                <div
                  key={item._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    {item.material?.image ? (
                      <img
                        src={item.material.image}
                        alt={item.material.name}
                        className="h-12 w-12 object-cover rounded-md"
                      />
                    ) : (
                      <div className="h-12 w-12 bg-gray-200 rounded-md flex items-center justify-center">
                        <span className="text-gray-500 text-xs">No Image</span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {item.material?.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {item.material?.category
                          ?.replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      {item.quantity}kg × ₦
                      {item.material?.pricePerKg?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-lg font-semibold text-green-600">
                      ₦
                      {(
                        (item.quantity || 0) * (item.material?.pricePerKg || 0)
                      ).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Total Quantity: {binData?.totalQuantity}
                    kg
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-green-600">
                    ₦{binData?.totalPrice?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-sm text-gray-600">Total Amount</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Account Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Select Payment Account
              </h2>
              <button
                type="button"
                onClick={() => navigate("/banks")}
                className="text-sm font-semibold text-green-700"
              >
                Add another account
              </button>
            </div>

            {banks?.data?.bankDetails?.length === 0 ? (
              <div className="text-center py-8">
                <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No bank accounts found</p>
                <button
                  onClick={() => navigate("/banks")}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Add Bank Account
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {banks?.data?.bankDetails?.map((bank) => (
                  <label
                    key={bank._id}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedBank === bank._id
                        ? 'border-green-300 bg-green-50 hover:bg-green-100'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bank"
                      value={bank._id}
                      checked={selectedBank === bank._id}
                      onChange={(e) => setSelectedBank(e.target.value)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">
                        {bank.bankName}
                      </p>
                      <p className="text-sm text-gray-600">
                        {bank.accountName} - {bank.accountNumber}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          {(banks?.data?.bankDetails?.length === 0 && binData?.materials) && (
            <div>
              <p>
                It looks like you haven’t added any bank details yet. To
                complete your payment smoothly, please add your bank account
                information.
              </p>
              <button
                onClick={() => navigate("/banks")}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Add Bank Account
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {/* Proceed to Sell Button */}
              <button
                onClick={handleSell}
                disabled={
                  selling ||
                  !selectedBank ||
                  !isBinValidated ||
                  binData?.sold
                }
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-md transition-colors"
              >
                {selling ? "Completing Sale..." : "Proceed to Sell"}
              </button>

              
            </div>

            {!isBinValidated && (
              <p className="text-sm text-gray-600 mt-4 text-center">
                Your bin must be validated by an agent before you can complete
                the sale.
              </p>
            )}
          </div>

          {/* Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Sale Process
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>Review your validated materials and quantities above</li>
              <li>Select the bank account where you want to receive payment</li>
              <li>Click "Complete Sale" to finalize your transaction</li>
              <li>
                Payment will be processed and sent to your selected bank account
              </li>
              <li>You can track payment status in your sale history</li>
            </ol>
          </div>
        </>
      )}

      {/* Chat Modal */}
      <ChatModal 
        binId={chatBinId} 
        isOpen={showChat} 
        onClose={() => setShowChat(false)} 
      />
    </div>
  );
};

export default Sell;
